import type { ReactNode } from "react";

import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";

export interface SettingsColumn<T> {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
}

export function SettingsTable<T extends { id: number }>({
  title,
  rows,
  columns,
  addLabel = "Add",
  onAdd,
  onEdit,
  onDeactivate,
}: {
  title: string;
  rows: T[];
  columns: SettingsColumn<T>[];
  addLabel?: string;
  onAdd: () => void;
  onEdit: (row: T) => void;
  onDeactivate: (row: T) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>{title}</CardTitle>
          <Button onClick={onAdd}>{addLabel}</Button>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="text-left text-xs uppercase tracking-[0.12em] text-slate-500">
            <tr>
              {columns.map((column) => (
                <th key={column.key} className="py-2 pr-4">{column.label}</th>
              ))}
              <th className="py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row) => (
              <tr key={row.id} className={"is_active" in row && !row.is_active ? "opacity-60" : ""}>
                {columns.map((column) => (
                  <td key={column.key} className="py-3 pr-4 align-top">{column.render(row)}</td>
                ))}
                <td className="py-3 text-right">
                  <div className="inline-flex gap-2">
                    <Button variant="secondary" onClick={() => onEdit(row)}>Edit</Button>
                    <Button variant="ghost" onClick={() => onDeactivate(row)}>Deactivate</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No rows yet.</div>
        ) : null}
      </CardContent>
    </Card>
  );
}
